import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';

// 懒加载页面组件，实现代码分割，减少初始加载体积
const Workbench = React.lazy(() => import('./pages/Workbench'));
const TaskList = React.lazy(() => import('./pages/TaskList'));
const TaskDetail = React.lazy(() => import('./pages/TaskDetail'));
const Statistics = React.lazy(() => import('./pages/Statistics'));
const Users = React.lazy(() => import('./pages/Users'));
const Projects = React.lazy(() => import('./pages/Projects'));
const Subjects = React.lazy(() => import('./pages/Subjects'));
const Groups = React.lazy(() => import('./pages/Groups'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Announcements = React.lazy(() => import('./pages/Announcements'));
const PersonalTasks = React.lazy(() => import('./pages/PersonalTasks'));

const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <Spin size="large" tip="加载中..." />
  </div>
);

function PrivateRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: string[] }) {
  const { user, loading } = useAuth();

  if (loading) return <LoadingFallback />;
  if (!user) return <Navigate to="/login" />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" />;

  return <Layout>{children}</Layout>;
}

function App() {
  return (
    <ConfigProvider locale={zhCN} theme={{ token: { colorPrimary: '#43a047' } }}>
      <AuthProvider>
        <Router>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<PrivateRoute><Workbench /></PrivateRoute>} />
              <Route path="/tasks" element={<PrivateRoute><TaskList /></PrivateRoute>} />
              <Route path="/tasks/:id" element={<PrivateRoute><TaskDetail /></PrivateRoute>} />
              <Route path="/statistics" element={<PrivateRoute allowedRoles={['admin', 'supervisor', 'leader']}><Statistics /></PrivateRoute>} />
              <Route path="/users" element={<PrivateRoute allowedRoles={['admin']}><Users /></PrivateRoute>} />
              <Route path="/projects" element={<PrivateRoute allowedRoles={['admin', 'supervisor']}><Projects /></PrivateRoute>} />
              <Route path="/subjects" element={<PrivateRoute allowedRoles={['admin', 'supervisor']}><Subjects /></PrivateRoute>} />
              <Route path="/groups" element={<PrivateRoute allowedRoles={['admin', 'supervisor']}><Groups /></PrivateRoute>} />
              <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/announcements" element={<PrivateRoute><Announcements /></PrivateRoute>} />
              <Route path="/personal-tasks" element={<PrivateRoute><PersonalTasks /></PrivateRoute>} />
            </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
    </ConfigProvider>
  );
}

export default App;
